<template>
  <div class="system-log-page">
    <template v-if="allowed">
      <vx-card class="mb-6">
        <form class="log-filter-form" @submit.prevent="applyFilters">
          <div class="log-filters">
            <label
              >อีเมล<input
                v-model="draft.email"
                maxlength="200"
                placeholder="อีเมลผู้ใช้งาน"
                @input="scheduleSearch"
                @compositionstart="scheduleSearch({ isComposing: true })"
                @compositionend="scheduleSearch"
            /></label>
            <label
              >ตั้งแต่วันที่ (เวลาไทย)<input
                v-model="draft.dateFrom"
                type="date"
                @change="applyFilters"
            /></label>
            <label
              >ถึงวันที่ (เวลาไทย)<input
                v-model="draft.dateTo"
                type="date"
                @change="applyFilters"
            /></label>
            <label v-for="field in config.filters" :key="field.key"
              >{{ field.label }}
              <select
                v-if="field.options"
                v-model="draft[field.key]"
                @change="applyFilters"
              >
                <option value="">ทั้งหมด</option>
                <option
                  v-for="option in field.options"
                  :key="option.value || option"
                  :value="option.value || option"
                >
                  {{ option.label || option }}
                </option>
              </select>
              <input
                v-else
                v-model="draft[field.key]"
                maxlength="200"
                @input="scheduleSearch"
                @compositionstart="scheduleSearch({ isComposing: true })"
                @compositionend="scheduleSearch"
              />
            </label>
          </div>
          <div class="log-filter-actions">
            <button type="button" @click="clearFilters">ล้างตัวกรอง</button>
          </div>
        </form>
      </vx-card>
      <vx-card>
        <div class="log-toolbar">
          <span aria-live="polite">{{
            loading || debouncePending
              ? 'กำลังค้นหา…'
              : 'ทั้งหมด ' + total + ' รายการ'
          }}</span>
          <label class="log-search">
            <input
              v-model="draft.search"
              aria-label="ค้นหาข้อมูลหรืออีเมล"
              maxlength="200"
              placeholder="ค้นหาข้อมูลหรืออีเมล"
              @input="scheduleSearch"
              @keyup="scheduleSearch"
              @compositionstart="scheduleSearch({ isComposing: true })"
              @compositionend="scheduleSearch"
            />
          </label>
        </div>
        <p v-if="error" role="alert" class="text-danger mb-4">{{ error }}</p>
        <div class="log-table-scroll" :aria-busy="loading || debouncePending">
          <table class="log-table">
            <thead>
              <tr>
                <th v-for="col in config.columns" :key="col.key">
                  {{ col.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.id"
                class="log-row"
                tabindex="0"
                aria-label="เปิดรายละเอียดรายการ"
                @click="openDetails(row)"
                @keydown.enter.prevent="openDetails(row)"
                @keydown.space.prevent="openDetails(row)"
              >
                <td v-for="col in config.columns" :key="col.key">
                  {{ display(row, col.key) }}
                </td>
              </tr>
              <tr v-if="!loading && !debouncePending && !rows.length">
                <td :colspan="config.columns.length">
                  {{ error ? 'โหลดข้อมูลไม่สำเร็จ' : 'ไม่พบข้อมูล' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="log-pagination">
          <label class="log-page-size"
            >รายการต่อหน้า:
            <span class="log-select-wrap">
              <select
                aria-label="รายการต่อหน้า"
                :value="pageSize"
                :disabled="loading || debouncePending"
                @change="changeSize($event.target.value)"
              >
                <option
                  v-for="size in [5, 10, 20, 50, 100]"
                  :key="size"
                  :value="size"
                >
                  {{ size }}
                </option>
              </select>
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="m7 10 5 5 5-5" />
              </svg>
            </span>
          </label>
          <span class="log-page-range" aria-live="polite"
            >{{ total && rows.length ? (page - 1) * pageSize + 1 : 0 }}-{{
              rows.length ? (page - 1) * pageSize + rows.length : 0
            }}
            จาก {{ total }}</span
          >
          <nav class="log-page-buttons" aria-label="การแบ่งหน้าประวัติ">
            <button
              class="log-page-button"
              type="button"
              aria-label="หน้าแรก"
              title="หน้าแรก"
              :disabled="loading || debouncePending || page <= 1 || !!error"
              @click="load(1)"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M5 5v14 M19 5L8 12l11 7Z" />
              </svg>
            </button>
            <button
              class="log-page-button"
              type="button"
              aria-label="หน้าก่อนหน้า"
              title="หน้าก่อนหน้า"
              :disabled="loading || debouncePending || page <= 1 || !!error"
              @click="load(page - 1)"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="m15 6-6 6 6 6" />
              </svg>
            </button>
            <button
              class="log-page-button"
              type="button"
              aria-label="หน้าถัดไป"
              title="หน้าถัดไป"
              :disabled="
                loading || debouncePending || page >= totalPages || !!error
              "
              @click="load(page + 1)"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="m9 6 6 6-6 6" />
              </svg>
            </button>
            <button
              class="log-page-button"
              type="button"
              aria-label="หน้าสุดท้าย"
              title="หน้าสุดท้าย"
              :disabled="
                loading || debouncePending || page >= totalPages || !!error
              "
              @click="load(totalPages)"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M19 5v14 M5 5l11 7-11 7Z" />
              </svg>
            </button>
          </nav>
        </div>
      </vx-card>
      <vs-popup title="รายละเอียดประวัติระบบ" :active.sync="detailOpen">
        <template v-if="selectedRow">
          <p class="mb-4">
            ข้อมูลที่แสดงผ่านการปิดบังข้อมูลละเอียดอ่อนจาก Backend แล้ว
          </p>
          <dl class="log-details">
            <template v-for="col in config.columns"
              ><dt :key="col.key + '-label'">{{ col.label }}</dt>
              <dd :key="col.key">
                {{ display(selectedRow, col.key) }}
              </dd></template
            >
          </dl>
          <pre class="log-detail-json">{{
            pretty(JSON.stringify(selectedRow))
          }}</pre>
        </template>
      </vs-popup>
    </template>
    <p v-else role="alert">ไม่มีสิทธิ์ดูประวัติระบบ</p>
  </div>
</template>
<script>
import moment from 'moment'
import { apolloClient } from '@/setup/apollo-setup'
import { logPages, logQuery, emptyFilter } from './config'

export default {
  props: {
    kind: {
      type: String,
      required: true,
      validator: (value) =>
        Object.prototype.hasOwnProperty.call(logPages, value),
    },
  },
  data() {
    return {
      draft: emptyFilter(this.kind),
      applied: {},
      rows: [],
      total: 0,
      page: 1,
      pageSize: 10,
      loading: false,
      error: '',
      selectedRow: null,
      detailOpen: false,
      searchTimer: null,
      debouncePending: false,
      requestId: 0,
    }
  },
  computed: {
    config() {
      return logPages[this.kind]
    },
    allowed() {
      return this.isRoot || this.pms(this.config.permission, 'visible', 'b')
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
  },
  created() {
    if (this.allowed) this.load(1)
  },
  beforeDestroy() {
    this.cancelSearch()
    this.requestId += 1
  },
  methods: {
    cancelSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
      this.debouncePending = false
    },
    scheduleSearch(event) {
      this.cancelSearch()
      this.requestId += 1
      this.loading = false
      this.rows = []
      this.detailOpen = false
      this.selectedRow = null
      this.debouncePending = true
      if (event && event.isComposing) return
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null
        this.debouncePending = false
        this.applyFilters()
      }, 400)
    },
    openDetails(row) {
      this.selectedRow = row
      this.detailOpen = true
    },
    applyFilters() {
      this.cancelSearch()
      this.requestId += 1
      this.loading = false
      this.rows = []
      this.detailOpen = false
      this.selectedRow = null
      if (
        this.draft.dateFrom &&
        this.draft.dateTo &&
        this.draft.dateFrom > this.draft.dateTo
      ) {
        this.error = 'วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด'
        return
      }
      this.applied = Object.fromEntries(
        Object.entries(this.draft)
          .map(([key, value]) => [key, value.trim()])
          .filter(([, value]) => value)
      )
      this.load(1)
    },
    clearFilters() {
      this.draft = emptyFilter(this.kind)
      this.applied = {}
      this.load(1)
    },
    changeSize(value) {
      this.pageSize = Number(value)
      this.load(1)
    },
    async load(page) {
      if (!this.allowed) return
      this.cancelSearch()
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      this.rows = []
      this.selectedRow = null
      this.detailOpen = false
      try {
        const { data } = await apolloClient.query({
          query: logQuery(this.kind),
          variables: {
            kind: this.kind,
            page,
            pageSize: this.pageSize,
            filter: { ...this.applied },
            sortBy: 'createdAt',
            sortDirection: 'desc',
          },
          fetchPolicy: 'no-cache',
        })
        if (requestId !== this.requestId) return
        const result = data.getSystemLogPage
        this.rows = result.items
        this.total = result.total
        this.page = result.page
      } catch (_) {
        if (requestId !== this.requestId) return
        this.rows = []
        this.total = 0
        this.error =
          'โหลดประวัติไม่สำเร็จ กรุณาตรวจสอบสิทธิ์ การเชื่อมต่อ หรือเปลี่ยนตัวกรองเพื่อลองใหม่'
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    display(row, key) {
      if (key === 'createdAt')
        return moment(row.createdAt).utcOffset(7).format('DD/MM/YYYY HH:mm:ss')
      if (this.kind === 'importLog' && key === 'success')
        return `${row.success}/${row.row}`
      if (this.kind === 'importLog' && key === 'status')
        return row.status === 'true' ? 'ปกติ' : 'ยกเลิก'
      return row[key] == null ? '' : row[key]
    },
    pretty(value) {
      if (!value) return ''
      try {
        return JSON.stringify(JSON.parse(value), null, 2)
      } catch (_) {
        return value
      }
    },
  },
}
</script>
<style scoped>
.log-filter-form {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}
.log-filters {
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
  gap: 16px;
}
.log-filters label {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.system-log-page input,
.system-log-page select {
  padding: 9px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background: white;
  color: inherit;
}
.system-log-page button {
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 8px 12px;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.system-log-page button:disabled {
  opacity: 0.5;
  cursor: default;
}
.log-filter-actions,
.log-toolbar,
.log-pagination {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}
.log-filter-actions {
  flex: 0 0 auto;
  margin: 0;
  align-self: flex-end;
  white-space: nowrap;
}
.log-toolbar {
  justify-content: space-between;
  margin-bottom: 20px;
}
.log-table-scroll {
  overflow-x: auto;
}
.log-table {
  width: 100%;
  border-collapse: collapse;
}
.log-table th,
.log-table td {
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid #eee;
  overflow-wrap: anywhere;
  min-width: 110px;
  max-width: 400px;
}
.log-detail-json {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 450px;
  overflow-y: auto;
}
.log-pagination {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #ebe9ee;
  justify-content: flex-end;
  gap: 24px;
  color: #777383;
  font-size: 16px;
}
.log-page-size {
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
}
.log-select-wrap {
  position: relative;
  display: inline-flex;
}
.system-log-page .log-select-wrap select {
  appearance: none;
  width: 110px;
  height: 42px;
  padding: 8px 38px 8px 20px;
  border: 1px solid #d8d6de;
  border-radius: 8px;
  color: #575262;
  font: inherit;
}
.log-select-wrap svg {
  pointer-events: none;
  position: absolute;
  right: 12px;
  top: 11px;
  width: 20px;
  height: 20px;
}
.log-page-range {
  white-space: nowrap;
}
.log-page-buttons {
  display: flex;
  align-items: center;
  gap: 16px;
}
.system-log-page .log-page-button {
  width: 40px;
  height: 40px;
  padding: 8px;
  border: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #8c8797;
}
.log-page-button svg {
  width: 24px;
  height: 24px;
}
.log-pagination svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.system-log-page .log-page-button:disabled {
  opacity: 0.55;
}
.system-log-page .log-page-button:not(:disabled):hover {
  background: #f4f2ff;
}
.log-page-button:focus-visible,
.log-select-wrap select:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 2px;
}
@media (max-width: 600px) {
  .log-pagination {
    gap: 14px;
    font-size: 14px;
  }
  .log-page-buttons {
    gap: 8px;
  }
}
.log-row {
  cursor: pointer;
}
.log-row:hover,
.log-row:focus {
  background: #f4f2ff;
}
.log-search {
  display: block;
  width: min(100%, 420px);
  margin-left: auto;
}
.log-search input {
  width: 100%;
  box-sizing: border-box;
}
.log-details {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 12px;
  overflow-wrap: anywhere;
}
.log-detail-json {
  margin-top: 20px;
}
</style>
